// Model A: Local-first vault via File System Access API
// Chromium-only (Chrome, Edge, Arc, Brave)

import { saveHandle, getHandle, clearHandle } from "./vault-idb";
import { emitSignal } from "./signal";

export function isFileSystemAccessSupported() {
  if (typeof window === "undefined") return false;
  if (!("showDirectoryPicker" in window)) return false;
  // Mobile Chrome exposes showDirectoryPicker but it doesn't work — exclude mobile
  const ua = navigator.userAgent || "";
  if (/Android|iPhone|iPad|iPod|Mobile/i.test(ua)) return false;
  return true;
}

// Exported so tests can pin the folder set against future name normalization
// (auto-kebab, lowercase, etc.). "Ah Fulkit" must keep its space — it's a
// user-visible folder name and the spec confirmed Finder sort order works.
export const REQUIRED_FOLDERS = [
  "_FULKIT", "00-INBOX", "01-PERSONAL", "02-BUSINESS",
  "03-PROJECTS", "04-DEV", "05-IDEAS", "06-LEARNING", "07-ARCHIVE",
  "Ah Fulkit",
];

// V2 Step 9 — bulletproof brain/trunk. Canonical names + smoking-gun
// files used to detect rename drift. _FULKIT/ is the brain (engine
// room — ledger, pulse, profile, README). Ah Fulkit/ is the trunk
// (welcome materials — VAULT-GUIDE, thank-you). Either getting deleted
// or renamed is recoverable; auto-restore reverses the attempt.
export const BRAIN_FOLDER = "_FULKIT";
export const TRUNK_FOLDER = "Ah Fulkit";
export const BRAIN_SMOKING_GUN_FILES = ["ledger.md", "pulse.md"];
export const TRUNK_SMOKING_GUN_FILES = ["VAULT-GUIDE.md", "thank-you.md"];

// Walk the vault root and report on _FULKIT/ + Ah Fulkit/ state.
// Pure walker (no writes). Smoking-gun match: any non-canonical
// folder containing one of the brain or trunk gun-files is treated
// as a renamed copy of that folder. First match wins (deterministic
// for the rename-back step).
//
// Returns { brain, trunk } where each is one of:
//   "ok"                              — canonical folder exists
//   "missing"                         — neither canonical nor renamed
//   { renamedFrom: "<folder name>" }  — found a rename via smoking gun
export async function detectBrainTrunkDrift(handle) {
  let brainCanonical = false;
  let trunkCanonical = false;
  let brainRenamedFrom = null;
  let trunkRenamedFrom = null;
  for await (const [name, item] of handle.entries()) {
    if (item.kind !== "directory") continue;
    if (name === BRAIN_FOLDER) { brainCanonical = true; continue; }
    if (name === TRUNK_FOLDER) { trunkCanonical = true; continue; }
    if (!brainRenamedFrom && (await directoryContainsAnyFile(item, BRAIN_SMOKING_GUN_FILES))) {
      brainRenamedFrom = name;
      continue;
    }
    if (!trunkRenamedFrom && (await directoryContainsAnyFile(item, TRUNK_SMOKING_GUN_FILES))) {
      trunkRenamedFrom = name;
    }
  }
  return {
    brain: brainCanonical ? "ok" : (brainRenamedFrom ? { renamedFrom: brainRenamedFrom } : "missing"),
    trunk: trunkCanonical ? "ok" : (trunkRenamedFrom ? { renamedFrom: trunkRenamedFrom } : "missing"),
  };
}

async function directoryContainsAnyFile(dirHandle, filenames) {
  for (const fn of filenames) {
    try {
      await dirHandle.getFileHandle(fn);
      return true;
    } catch { /* not found, try next */ }
  }
  return false;
}

// Restore brain/trunk based on a drift report. Pure side-effect helper:
// emits the four V2 Step 9 signals, creates canonical folders, moves
// renamed-folder contents into canonical destination, removes the
// renamed shell. Brain/trunk folders are flat per spec (README.md,
// ledger.md, etc. — no nested dirs), so the copy walks files only.
//
// Idempotent: if the canonical folder already has a file that the
// renamed folder ALSO has, the renamed-folder copy wins (preserves
// user edits when they renamed the folder while leaving the file
// intact). Always-rebuild content for Fülkit-managed files
// (ledger/pulse/profile) gets re-applied later by ensureVaultBootstrap.
export async function restoreBrainTrunk(handle, drift) {
  const result = { brainRestored: false, trunkRestored: false };
  if (drift?.brain && drift.brain !== "ok") {
    await restoreOneFolder(handle, drift.brain, BRAIN_FOLDER, "brain");
    result.brainRestored = true;
  }
  if (drift?.trunk && drift.trunk !== "ok") {
    await restoreOneFolder(handle, drift.trunk, TRUNK_FOLDER, "trunk");
    result.trunkRestored = true;
  }
  return result;
}

async function restoreOneFolder(rootHandle, state, canonicalName, kind) {
  const renamedFrom = (typeof state === "object") ? state.renamedFrom : null;
  const detectedState = renamedFrom ? `renamed_to_${renamedFrom}` : "missing";
  // Literal signal names per kind so radio-reconcile's static-grep
  // catches them. Don't refactor to a computed signal name without
  // also updating reconcile's manual-register list.
  if (kind === "brain") {
    emitSignal("vault_brain_drift_detected", "warning", { detected_state: detectedState });
  } else {
    emitSignal("vault_trunk_drift_detected", "warning", { detected_state: detectedState });
  }
  const canonical = await rootHandle.getDirectoryHandle(canonicalName, { create: true });
  if (renamedFrom) {
    try {
      const renamed = await rootHandle.getDirectoryHandle(renamedFrom);
      // Brain + trunk are flat folders; iterate files only.
      for await (const [name, item] of renamed.entries()) {
        if (item.kind !== "file") continue;
        try {
          const file = await item.getFile();
          const dest = await canonical.getFileHandle(name, { create: true });
          const writable = await dest.createWritable();
          await writable.write(await file.arrayBuffer());
          await writable.close();
        } catch (copyErr) {
          // Per-file copy failure shouldn't kill the whole restore;
          // bootstrap will rebuild Fülkit-managed files anyway.
          emitSignal("local_vault_write_failed", "error", {
            folder: canonicalName, filename: name, error: copyErr?.message || "unknown", phase: "drift_restore",
          });
        }
      }
      try {
        await rootHandle.removeEntry(renamedFrom, { recursive: true });
      } catch (rmErr) {
        emitSignal("local_vault_structure_failed", "warning", {
          folder: renamedFrom, error: rmErr?.message || "unknown", phase: "drift_remove_renamed",
        });
      }
    } catch (err) {
      emitSignal("local_vault_structure_failed", "warning", {
        folder: renamedFrom, error: err?.message || "unknown", phase: "drift_open_renamed",
      });
    }
  }
  if (kind === "brain") {
    emitSignal("vault_brain_auto_restored", "info", {
      restored_path: canonicalName, detected_state: detectedState,
    });
  } else {
    emitSignal("vault_trunk_auto_restored", "info", {
      restored_path: canonicalName, detected_state: detectedState,
    });
  }
}

// Validate vault structure — drift-aware. Detects + restores _FULKIT/
// and Ah Fulkit/ rename/delete drift first, then auto-creates any
// other REQUIRED_FOLDERS that are still missing. Content rebuild
// (ledger, pulse, profile, README, VAULT-GUIDE, thank-you) is the
// caller's responsibility — see lib/vault-bootstrap.js.
export async function validateVaultStructure(handle) {
  // Step 9 — drift detection runs first so the rename-back happens
  // before the missing-folder loop tries to create canonical empty
  // _FULKIT/ alongside the renamed copy.
  try {
    const drift = await detectBrainTrunkDrift(handle);
    if (drift.brain !== "ok" || drift.trunk !== "ok") {
      await restoreBrainTrunk(handle, drift);
    }
  } catch (err) {
    emitSignal("local_vault_structure_failed", "warning", {
      folder: "_drift_detection",
      error: err?.message || "unknown",
      name: err?.name || null,
    });
    // Don't throw — fall through to required-folder loop so other
    // missing folders still get created even if drift detection
    // hits an unexpected error.
  }

  const missing = [];
  for (const folder of REQUIRED_FOLDERS) {
    try {
      await handle.getDirectoryHandle(folder);
    } catch {
      try {
        await handle.getDirectoryHandle(folder, { create: true });
        missing.push(folder);
      } catch (err) {
        emitSignal("local_vault_structure_failed", "warning", {
          folder,
          error: err?.message || "unknown",
          name: err?.name || null,
        });
        throw err;
      }
    }
  }
  return missing;
}

export async function pickVaultDirectory(userId) {
  const handle = await window.showDirectoryPicker({ mode: "readwrite" });
  await validateVaultStructure(handle);
  await saveHandle(userId, handle);
  return handle;
}

// Show folder picker WITHOUT side effects — no validation, no IDB save.
// Caller classifies + confirms before committing via commitVaultDirectory.
// Returns null if user cancels.
export async function pickDirectory() {
  if (!isFileSystemAccessSupported()) return null;
  try {
    return await window.showDirectoryPicker({ mode: "readwrite" });
  } catch (e) {
    if (e?.name === "AbortError") return null;
    throw e;
  }
}

// Shallow-classify a picked folder without mutating it:
//   "empty"       — zero entries
//   "valid_vault" — has _FULKIT/ directory (already a Fülkit vault)
//   "obsidian"    — has .obsidian (Obsidian vault, no _FULKIT)
//   "unknown"     — has entries but no vault markers
export async function classifyFolder(handle) {
  let hasObsidianConfig = false;
  let hasVaultMarker = false;
  let entryCount = 0;
  for await (const [name, item] of handle.entries()) {
    entryCount++;
    if (name === ".obsidian") hasObsidianConfig = true;
    if (name === "_FULKIT" && item.kind === "directory") hasVaultMarker = true;
  }
  if (entryCount === 0) return "empty";
  if (hasVaultMarker) return "valid_vault";
  if (hasObsidianConfig) return "obsidian";
  return "unknown";
}

// Commit a previously-picked handle: ensure REQUIRED_FOLDERS exist, persist
// handle to IDB for restore on next session. Caller is responsible for
// updating VaultProvider state (see vault.setConnectedDirectory).
export async function commitVaultDirectory(userId, handle) {
  await validateVaultStructure(handle);
  await saveHandle(userId, handle);
  return handle;
}

export async function restoreDirectoryHandle(userId) {
  const handle = await getHandle(userId);
  if (!handle) return null;

  // Re-verify permission — may prompt user after browser restart.
  // Non-granted here is the #1 silent failure mode: user's vault is
  // effectively disconnected until they re-grant, and without a signal
  // neither they nor we know.
  const permission = await handle.requestPermission({ mode: "readwrite" });
  if (permission !== "granted") {
    emitSignal("local_vault_permission_lost", "warning", { permission });
    return null;
  }

  await validateVaultStructure(handle);
  return handle;
}

export async function disconnectVault(userId) {
  await clearHandle(userId);
}

// Recursively read all .md files from a directory handle
export async function readLocalVault(handle) {
  const files = [];
  await readDirectory(handle, "", files);
  return files;
}

// Walk a slash-separated folder path, calling getDirectoryHandle once
// per segment. The FS Access API's getDirectoryHandle accepts only
// single-segment names — a slash in the argument throws "Name is not
// allowed." Disk-import already routes nested paths through the read
// side; this is the write-side equivalent so writeLocalNote and
// writeBinaryFile can target nested folders symmetrically.
//
// Per-segment getDirectoryHandle({create:true}) creates each missing
// level. Empty/trailing/leading slashes are filtered. Returns the
// deepest handle reached.
//
// Exported for direct use by other writers; called internally by
// writeLocalNote + writeBinaryFile below.
export async function getNestedDirectoryHandle(rootHandle, folderPath) {
  if (!rootHandle) throw new Error("getNestedDirectoryHandle: rootHandle required");
  if (!folderPath) return rootHandle;
  // Sanitize each segment at the FS Access boundary: defense-in-depth
  // against names that survive visible-char inspection but trip Chrome's
  // ValidatePathComponent (control chars, zero-width, decomposed Unicode).
  // Second .filter(Boolean) drops segments that fully reduce to empty.
  const segments = String(folderPath).split("/").filter(Boolean).map(sanitizeFilename).filter(Boolean);
  if (segments.length === 0) return rootHandle;
  let current = rootHandle;
  for (const segment of segments) {
    current = await current.getDirectoryHandle(segment, { create: true });
  }
  return current;
}

// Write a note as a .md file to the vault folder
export async function writeLocalNote(handle, folder, title, content) {
  if (!handle) throw new Error("No vault connected");
  const safeName = sanitizeFilename(title) + ".md";
  const targetFolder = folder || "00-INBOX";
  try {
    const dirHandle = await getNestedDirectoryHandle(handle, targetFolder);
    const fileHandle = await dirHandle.getFileHandle(safeName, { create: true });
    const writable = await fileHandle.createWritable();
    await writable.write(content);
    await writable.close();
    return `${targetFolder}/${safeName}`;
  } catch (err) {
    emitSignal("local_vault_write_failed", "error", {
      folder: targetFolder,
      file: safeName,
      error: err?.message || "unknown",
      name: err?.name || null,
    });
    throw err;
  }
}

// FS Access primitives reused across vault modules (ledger, pulse,
// writeback). Live here because vault-local is the FS-handle layer.
// sanitizeFilename source-of-truth lives in vault-paths.js (zero deps);
// re-exported here for back-compat with existing callers.
export { sanitizeFilename } from "./vault-paths";
import { sanitizeFilename } from "./vault-paths";

export async function writeFile(directoryHandle, folder, filename, content) {
  const dir = await directoryHandle.getDirectoryHandle(folder, { create: true });
  const fileHandle = await dir.getFileHandle(filename, { create: true });
  const writable = await fileHandle.createWritable();
  await writable.write(content);
  await writable.close();
}

export async function appendOrCreate(directoryHandle, folder, filename, header, newContent) {
  const dir = await directoryHandle.getDirectoryHandle(folder, { create: true });
  let existing = "";
  try {
    const fh = await dir.getFileHandle(filename);
    const file = await fh.getFile();
    existing = await file.text();
  } catch {
    // File doesn't exist yet — expected on first write
  }
  const content = existing ? `${existing}\n\n${newContent}` : `${header}\n\n${newContent}`;
  const fileHandle = await dir.getFileHandle(filename, { create: true });
  const writable = await fileHandle.createWritable();
  await writable.write(content);
  await writable.close();
}

// Write a binary file (image, PDF, etc.) to a vault subfolder. Decodes
// base64 to bytes client-side; FS Access API handles the rest. Used by
// V2 step 5 vaultBinaryWrite SSE handler in use-chat.js.
//
// Detects cross-round filename collisions (existing file at target path)
// and emits vault_binary_filename_collision (info) before overwriting.
// Pure telemetry — overwrite still happens. If dogfood data shows frequent
// firing, the deferred collision-handling path becomes a real follow-up.
export async function writeBinaryFile(handle, folder, filename, dataBase64, mediaType) {
  if (!handle) throw new Error("No vault connected");
  const targetFolder = folder || "00-INBOX";
  try {
    const dir = await getNestedDirectoryHandle(handle, targetFolder);

    // Probe for existing file BEFORE creating. getFileHandle without
    // create:true throws if the file doesn't exist — we use that as the
    // collision signal.
    let collisionDetected = false;
    try {
      await dir.getFileHandle(filename);
      collisionDetected = true;
    } catch {
      // File doesn't exist — first-time write, no collision
    }

    const fileHandle = await dir.getFileHandle(filename, { create: true });
    const writable = await fileHandle.createWritable();
    // Decode base64 → Uint8Array → Blob (browser-side only; atob is global)
    const binary = atob(dataBase64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    await writable.write(new Blob([bytes], { type: mediaType || "application/octet-stream" }));
    await writable.close();

    if (collisionDetected) {
      emitSignal("vault_binary_filename_collision", "info", {
        folder: targetFolder,
        filename,
        media_type: mediaType || "application/octet-stream",
      });
    }

    return `${targetFolder}/${filename}`;
  } catch (err) {
    emitSignal("vault_binary_write_failed", "warning", {
      phase: "client_disk_write",
      folder: targetFolder,
      file: filename,
      error: err?.message || "unknown",
      name: err?.name || null,
    });
    throw err;
  }
}

// Delete a note from the vault folder
export async function deleteLocalNote(handle, folder, title) {
  if (!handle) return;
  const safeName = sanitizeFilename(title) + ".md";
  try {
    const dirHandle = await handle.getDirectoryHandle(folder || "00-INBOX");
    await dirHandle.removeEntry(safeName);
  } catch {
    // File may not exist — that's fine
  }
}

async function readDirectory(dirHandle, path, files) {
  for await (const entry of dirHandle.values()) {
    const entryPath = path ? `${path}/${entry.name}` : entry.name;

    // Skip hidden directories/files
    if (entry.name.startsWith(".")) continue;

    if (entry.kind === "directory") {
      // Skip archive directories
      if (entry.name.toLowerCase().includes("archive")) continue;
      const subDir = await dirHandle.getDirectoryHandle(entry.name);
      await readDirectory(subDir, entryPath, files);
    } else if (entry.kind === "file") {
      if (entry.name.endsWith(".md")) {
        try {
          const file = await entry.getFile();
          const content = await file.text();
          files.push({
            title: entry.name.replace(/\.md$/, ""),
            content,
            path: entryPath,
          });
        } catch (err) {
          emitSignal("local_vault_read_failed", "warning", {
            path: entryPath,
            error: err?.message || "unknown",
            name: err?.name || null,
          });
        }
      }
    }
  }
}
